// src/hazir-beton/src/pages/CarsafSevkiyat.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  updateDoc,
  serverTimestamp,
  runTransaction,
  orderBy,
  limit,
} from "firebase/firestore";
import { createPortal } from "react-dom";
import { db, auth } from "../firebaseConfig";

// Drag & Drop
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const INOSEL_ID = "27517";

/* ================= HELPERS ================= */

const asText = (v) => String(v ?? "").trim();

const safeNum = (v) => {
  if (v === "" || v == null) return null;
  const n = Number(String(v).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};


const normPlate = (p) => asText(p).replace(/\s+/g, "").toUpperCase();

// DB ve UI için: "06ar0505" -> "06 AR 0505"
const formatPlate = (p) => {
  const raw = normPlate(p);
  return raw ? raw.replace(/[^A-Z0-9]/g, "") : "";
};


const pickMiktar = (x) => {
  // Koleksiyonlara göre miktar alan isimleri farklı olabiliyor, olabildiğince esnek oku.
  const cand =
    x?.dokumMiktar ??
    x?.dokum_miktar ??
    x?.m3 ??
    x?.miktar ??
    x?.toplam ??
    x?.fm3 ??
    x?.fiiliM3 ??
    x?.m3Toplam ??
    x?.m3Miktar ??
    x?.miktarM3;
  return safeNum(cand) ?? 0;
};

const fmtTr = (n) => {
  const v = Number(n) || 0;
  return v.toLocaleString("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};


/* Saat yazım kolaylığı */
const formatTimeSmart = (v) => {
  const d = String(v ?? "").replace(/\D/g, "");
  if (!d) return "";
  const s = d.length >= 4 ? d.slice(-4) : d.padStart(4, "0");
  return `${s.slice(0, 2)}:${s.slice(2, 4)}`;
};

const yesterdayISO = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};

const uniq = (arr) => [...new Set(arr)].filter(Boolean);

const parseHHMM = (hhmm) => {
  const t = String(hhmm ?? "").trim();
  if (!t) return null;
  const m = t.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
  return hh * 60 + mm;
};

const minutesToHHMM = (mins) => {
  if (mins == null) return "";
  const m = Math.max(0, Math.round(mins));
  const hh = String(Math.floor(m / 60)).padStart(2, "0");
  const mm = String(m % 60).padStart(2, "0");
  return `${hh}:${mm}`;
};

// İstenen: Fark her irsaliyede kendi votoKm alanına göre hesaplanır
// fark = votoKm - (Dönüş KM - Çıkış KM)
const calcFark = (r) => {
  const ck = safeNum(r?.cikisKm);
  const dk = safeNum(r?.donusKm);
  if (ck == null || dk == null) return "";
  const voto = safeNum(r?.votoKm) ?? 0;
  return voto - (dk - ck);
};

// İstenen: Süre = Dönüş Saat - Çıkış Saat (değiştirilemeyen, gece devri dahil)
const calcSure = (r) => {
  const s = parseHHMM(r?.cikisSaat);
  const e = parseHHMM(r?.donusSaat);
  if (s == null || e == null) return "";
  let d = e - s;
  if (d < 0) d += 24 * 60;
  return minutesToHHMM(d);
};

/* ================= EXCEL EXPORT (Excel açılır .xls) ================= */

const downloadExcel = (badRows) => {
  // ✅ En uyumlu yöntem: HTML tabloyu .xls olarak indir (Excel doğrudan açar)
  const headers = [
    "Tarih",
    "Tesis",
    "Plaka",
    "İrsaliye",
    "Şoför",
    "Çıkış Saat",
    "Çıkış KM",
    "Dönüş Saat",
    "Dönüş KM",
    "Müşteri",
    "Şantiye",
    "Hata Nedeni",
  ];

  const esc = (v) =>
    String(v ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

  const thead =
    "<tr>" + headers.map((h) => `<th>${esc(h)}</th>`).join("") + "</tr>";

  const tbody = badRows
    .map((r) => {
      const reason = r._errorReason || "Bilinmiyor";
      const cells = [
        r.tarih,
        r.tesis,
        r.mikserPlaka,
        r.irsaliyeNo,
        r.soforAdi,
        r.cikisKm,
        r.donusKm,
        r.cikisSaat,
        r.donusSaat,
        r.musteriAdi,
        r.santiyeAdi,
        reason,
      ];
      return "<tr>" + cells.map((c) => `<td>${esc(c)}</td>`).join("") + "</tr>";
    })
    .join("");

  const htmlDoc = `
    <html>
      <head>
        <meta charset="utf-8" />
      </head>
      <body>
        <table border="1">
          <thead>${thead}</thead>
          <tbody>${tbody}</tbody>
        </table>
      </body>
    </html>
  `.trim();

  const blob = new Blob(["\ufeff" + htmlDoc], {
    type: "application/vnd.ms-excel;charset=utf-8",
  });

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "hatali_kayitlar.xls";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

function FilterPopup({ anchor, values, selected, onToggle, onSelectAll, onClear, onClose }) {
  const [search, setSearch] = useState("");
  const ref = useRef(null);

  // Dışarı tıklayınca popup'ın kapanmasını sağlar
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  // Alfabetik sıralama ve arama kutusu süzgeci
  const filteredValues = useMemo(() => {
    return [...values]
      .sort((a, b) => String(a).localeCompare(String(b), "tr"))
      .filter((v) => String(v).toLowerCase().includes(search.toLowerCase()));
  }, [values, search]);

  if (!anchor) return null;

// 208. satırdaki FilterPopup fonksiyonunun içindeki return'ün altı:
  return createPortal(
    <div
      ref={ref}
      style={{
        position: "absolute",
        top: anchor.bottom + window.scrollY + 5,
        left: anchor.left + window.scrollX,
        background: "white",
        border: "1px solid #e2e8f0", 
        padding: "12px",
        zIndex: 9999,
        boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)", 
        borderRadius: "8px", 
        minWidth: "220px",
        maxHeight: "380px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Arama Kutusu */}
      <input
        autoFocus
        placeholder="Ara..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ marginBottom: "8px", padding: "6px", fontSize: "13px", border: "1px solid #ddd" }}
      />

      {/* Tümü / Temizle Butonları */}
      <div style={{ display: "flex", gap: "5px", marginBottom: "8px" }}>
        <button type="button" onClick={onSelectAll} style={{ fontSize: "11px", flex: 1, cursor: "pointer", padding: "4px" }}>
          Tümü
        </button>
        <button type="button" onClick={onClear} style={{ fontSize: "11px", flex: 1, cursor: "pointer", padding: "4px" }}>
          Temizle
        </button>
      </div>

      {/* Kaydırılabilir Liste Alanı */}
      <div style={{ overflowY: "auto", flex: 1, borderTop: "1px solid #eee", paddingTop: "5px" }}>
        {filteredValues.map((v) => (
          <label key={v} style={{ display: "flex", alignItems: "center", fontSize: "12px", cursor: "pointer", padding: "3px 0" }}>
            <input
              type="checkbox"
              checked={selected.includes(String(v))}
              onChange={() => onToggle(String(v))}
              style={{ marginRight: "6px" }}
            />
            {v || "(Boş)"}
          </label>
        ))}
      </div>

      {/* Kapat Butonu */}
      <button 
        type="button"
        onClick={onClose} 
        style={{ marginTop: "10px", width: "100%", background: "#f3f4f6", border: "1px solid #ccc", cursor: "pointer", padding: "6px", fontWeight: "bold" }}
      >
        Kapat
      </button>
    </div>,
    document.body
  );
}



/* ================= SORTABLE ROW ================= */

function SortableRow({ row, isBad, children }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: row._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    background: row?._lockByOther ? "#dff7df" : row?.tamamlandi ? "#f0f0f0" : isBad ? "#ffe1e1" : undefined,
  };

  return (
    <tr ref={setNodeRef} style={style}>
      {React.Children.map(children, (child, idx) => {
        if (idx === 0) {
          return (
            <td style={tdStyle}>
              <span
                title="Sürükle"
                style={{
                  cursor: "grab",
                  userSelect: "none",
                  padding: "0 6px 0 0",
                  display: "inline-block",
                }}
                {...attributes}
                {...listeners}
              >
                ⠿
              </span>
              {child}
              {row?._lockByOther ? (
                <div style={{ fontSize: 10, marginTop: 2, color: "#1b5e20" }} title={row._lockTitle || row._lockLabel || "Açık"}>
                  {row._lockLabel || "Açık"}
                </div>
              ) : null}
            </td>
          );
        }
        return <td style={tdStyle}>{child}</td>;
      })}
    </tr>
  );
}

/* ================= COMPONENT ================= */

export default function CarsafSevkiyat() {
  const [filterDate, setFilterDate] = useState(yesterdayISO());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [plateMap, setPlateMap] = useState({});

  const [plateToTesis, setPlateToTesis] = useState({});
  const [plakalar, setPlakalar] = useState([]);
  const [soforler, setSoforler] = useState([]);

  const [filters, setFilters] = useState({});
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [filterAnchor, setFilterAnchor] = useState(null);

  const [badRowIds, setBadRowIds] = useState([]);
  const [zoneStats, setZoneStats] = useState({ mikserCount: 0, mikserM3: 0, pompaCount: 0, pompaM3: 0 });
const [pompaRows, setPompaRows] = useState([]); // Pompa ilerlemesini de görmek için

  // Plaka bazlı: seçilen tarihten önceki EN SON tarihin en büyük Dönüş KM'si
  const [prevDayKmByPlate, setPrevDayKmByPlate] = useState({});
  const prevDayKmInFlightRef = useRef(new Set());

  // Sağ üst özet tablo (Tesis bazlı)
  const [ozetRows, setOzetRows] = useState([]);
  const [ozetTotals, setOzetTotals] = useState({ pompalar: 0, mikserler: 0, diger: 0, toplam: 0 });

// --- SATIR BAZLI KİLİT (Model B) ---
const lockOwnedRef = useRef(new Set()); // bu kullanıcı tarafından açılmış rowId'ler
const lockInFlightRef = useRef(new Set()); // aynı satıra tekrar tekrar kilit atmayı engeller

const currentEmail = auth.currentUser?.email || "";

const isLockByOther = (r) =>
  !!(
    r?.lock?.isOpen === true &&
    asText(r.lock.openedBy) &&
    asText(r.lock.openedBy) !== currentEmail
  );

const isLockByMe = (r) =>
  !!(
    r?.lock?.isOpen === true &&
    asText(r.lock.openedBy) &&
    asText(r.lock.openedBy) === currentEmail
  );

const lockLabel = (r) => {
  const by = asText(r?.lock?.openedBy);
  const at = r?.lock?.openedAt;
  let d = null;
  if (at && typeof at.toDate === "function") d = at.toDate();
  else if (at && typeof at.seconds === "number") d = new Date(at.seconds * 1000);
  else if (typeof at === "string" || typeof at === "number") d = new Date(at);

  const hhmm =
    d && !isNaN(d.getTime())
      ? `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
      : "";
  return by ? `Açık: ${by}${hhmm ? " • " + hhmm : ""}` : "Açık";
};

const lockTitle = (r) => {
  const by = asText(r?.lock?.openedBy);
  const at = r?.lock?.openedAt;
  let d = null;
  if (at && typeof at.toDate === "function") d = at.toDate();
  else if (at && typeof at.seconds === "number") d = new Date(at.seconds * 1000);
  else if (typeof at === "string" || typeof at === "number") d = new Date(at);
  const timeText = d && !isNaN(d.getTime()) ? d.toLocaleString("tr-TR") : "";
  return by ? `Kilitli: ${by}${timeText ? " - " + timeText : ""} tarihinde açtı` : "Kilitli";
};

const acquireRowLock = async (rowId) => {
  if (!rowId) return;
  if (!currentEmail) return;

  if (lockOwnedRef.current.has(rowId)) return;
  if (lockInFlightRef.current.has(rowId)) return;

  lockInFlightRef.current.add(rowId);
  try {
    const ref = doc(db, "hazirbeton_mikser_sevkiyat", rowId);

    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (!snap.exists()) return;

      const data = snap.data() || {};
      const lock = data.lock || {};
      const openedBy = asText(lock.openedBy);

      if (lock.isOpen === true && openedBy && openedBy !== currentEmail) {
        throw new Error("LOCKED_BY_OTHER");
      }

      tx.update(ref, {
        lock: {
          isOpen: true,
          openedBy: currentEmail,
          openedAt: serverTimestamp(),
        },
      });
    });

    lockOwnedRef.current.add(rowId);
    // local state (UI) güncelle
    setRows((prev) =>
      prev.map((x) =>
        x._id === rowId
          ? {
              ...x,
              lock: { isOpen: true, openedBy: currentEmail, openedAt: new Date() },
            }
          : x
      )
    );
  } catch (e) {
    // başka kullanıcı açmış olabilir - UI mevcut lock bilgisine göre yeşil olur
  } finally {
    lockInFlightRef.current.delete(rowId);
  }
};

const releaseRowLocks = async (ids) => {
  const list = (ids || []).filter(Boolean);
  if (!list.length) return;
  if (!currentEmail) return;

  for (const id of list) {
    try {
      await updateDoc(doc(db, "hazirbeton_mikser_sevkiyat", id), {
        lock: { isOpen: false, openedBy: "", openedAt: null },
      });
    } catch (e) {
    } finally {
      lockOwnedRef.current.delete(id);
    }
  }
};

// Sayfa kapanırken / component unmount olurken: açtığımız satır kilitlerini bırak (Model B)
useEffect(() => {
  const handler = () => {
    const ids = Array.from(lockOwnedRef.current);
    ids.forEach((id) => {
      try {
        updateDoc(doc(db, "hazirbeton_mikser_sevkiyat", id), {
          lock: { isOpen: false, openedBy: "", openedAt: null },
        });
      } catch (e) {}
    });
  };

  window.addEventListener("beforeunload", handler);
  return () => {
    window.removeEventListener("beforeunload", handler);
    const ids = Array.from(lockOwnedRef.current);
    if (ids.length) releaseRowLocks(ids);
  };
  // eslint-disable-next-line
}, [currentEmail]);

  /* -------- PLATES -------- */

  useEffect(() => {
    (async () => {
      const snap = await getDocs(collection(db, "plates"));
      const map = {};
      const list = [];

      snap.docs.forEach((d) => {
        const x = d.data();
        if (!x?.plate) return;
        const p = String(x.plate).replace(/\s+/g, "").toUpperCase();
        map[p] = x.tesis || x.tesisAdi || "";
        list.push(p);
      });

      //setPlateToTesis(map);
      setPlakalar(list.sort());
    })();
  }, []);


  /* -------- ÖNCEKİ GÜN MAX DÖNÜŞ KM (Plaka bazlı) -------- */

const fetchPrevDayMaxDonusKm = async (plateVal, currentDateStr) => {
  const pNorm = normPlate(plateVal);
  if (!pNorm || !currentDateStr) return null;

  try {
    const pFmt = formatPlate(pNorm);

    const snap = await getDocs(
      query(
        collection(db, "hazirbeton_mikser_sevkiyat"),
        where("deleted", "==", false),
        where("tamamlandi", "==", true), // 🔥 EN ÖNEMLİ EKLEME
        where("mikserPlaka", "in", [pNorm, pFmt])
      )
    );

    const all = snap.docs
      .map(d => d.data())
      .filter(x =>
        x &&
        x.tarihStr &&
        x.tarihStr < currentDateStr &&
        safeNum(x.donusKm) != null
      );

    if (!all.length) return null;

    // 🔥 En ileri tarihi bul
    const maxDate = all
      .map(x => x.tarihStr)
      .sort()
      .reverse()[0];

    const sameDay = all.filter(x => x.tarihStr === maxDate);

    const maxDonus = Math.max(
      ...sameDay.map(x => safeNum(x.donusKm))
    );

    return {
      forDate: currentDateStr,
      lastDate: maxDate,
      minCikisKm: maxDonus,
    };

  } catch (e) {
    console.error("KM fetch error:", e);
    return null;
  }
};





  // Seçili tarihte, her plakanın EN ÜST satırına (ilk görünen) otomatik Çıkış KM bas
  useEffect(() => {
    if (!filterDate) return;
    if (!rows || rows.length === 0) return;

    const seen = new Set(); // plakaNorm
    rows.forEach((r) => {
      const pNorm = normPlate(r.mikserPlaka);
      if (!pNorm) return;
      if (asText(r.tarihStr || filterDate) !== asText(filterDate)) return;

      if (seen.has(pNorm)) return;
      seen.add(pNorm);

      // sadece ilk satıra basılacak
      const rowId = r._id;
      // eğer kullanıcı elle doldurduysa boş değilse dokunma
      const cikisNum = safeNum(r.cikisKm);
      if (cikisNum != null) return;

      (async () => {
        const info = await fetchPrevDayMaxDonusKm(pNorm, filterDate);
        if (!info || info.minCikisKm == null) return;

        setRows((prev) => {
          const idx = prev.findIndex((x) => x._id === rowId);
          if (idx < 0) return prev;
          const rr = prev[idx];
          // kullanıcı sonra doldurmuş olabilir -> tekrar basma
          if (safeNum(rr.cikisKm) != null) return prev;
          const copy = [...prev];
          copy[idx] = { ...rr, cikisKm: String(info.minCikisKm) };
          return copy;
        });
      })();
    });
    // eslint-disable-next-line
  }, [filterDate, rows.length]);

  /* -------- ŞOFÖRLER -------- */

  const HAZIR_BETON_TESISLER = ["SİNCAN", "GÜVERCİNLİK", "ASKİ İVEDİK", "ONKOLOJİ"];
  const isAktif = (tarih = {}) => {
    const gi = Object.keys(tarih).filter((k) => k.startsWith("giris")).length;
    const ci = Object.keys(tarih).filter((k) => k.startsWith("cikis")).length;
    return gi > ci;
  };

  useEffect(() => {
    (async () => {
      const snap = await getDocs(collection(db, "personeller"));
      const list = snap.docs
        .map((d) => d.data())
        .filter((p) => {
          if (!p.ad) return false;
          const tesis = (p.tesis || "").trim().toLocaleUpperCase("tr-TR");
          if (!HAZIR_BETON_TESISLER.includes(tesis)) return false;
          return isAktif(p.tarih || {});
        })
        .map((p) => `${p.ad}${p.soyad ? " " + p.soyad : ""}`)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, "tr"));

      setSoforler(list);
    })();
  }, []);


  const buildOzetFromData = async (mikserSevkiyatList, tarihStr) => {
    try {
      // Mikserlerimiz: grid (hazirbeton_mikser_sevkiyat) üzerinden m3 toplamı
      const mikserByTesis = new Map();
      for (const r of mikserSevkiyatList || []) {
        const t = asText(r?.tesis);
        if (!t) continue;
        const v = safeNum(r?.m3) ?? 0;
        mikserByTesis.set(t, (mikserByTesis.get(t) || 0) + v);
      }

      // Pompalarımız: hazirbeton_pompa_hakedis (o tarihteki tüm dökümler; varsa pompaFirmaId==27517 filtrele)
      const pompaSnap = await getDocs(
        query(
          collection(db, "hazirbeton_pompa_hakedis"),
          where("tarihStr", "==", tarihStr)
        )
      );

 
      const pompaByTesis = new Map();
      pompaSnap.docs.forEach((d) => {
        const x = d.data() || {};
        const firmaOk =
          x.pompaFirmaId == null ? true : String(x.pompaFirmaId) === String(INOSEL_ID);
        if (!firmaOk) return;
        const t = asText(x.tesis);
        if (!t) return;
        const v = pickMiktar(x);
        pompaByTesis.set(t, (pompaByTesis.get(t) || 0) + v);
      });

      // Diğer: hazirbeton_zone_uretim (o tarihteki dökümler; mikserFirmaId==27517 veya pompaFirmaId==27517 olanları HARİÇ tut)
      const zoneSnap = await getDocs(
        query(
          collection(db, "hazirbeton_zone_uretim"),
          where("tarihStr", "==", tarihStr)
        )
      );

     /*
      const digerByTesis = new Map();
      zoneSnap.docs.forEach((d) => {
        const x = d.data() || {};
        const isOurMixer = x.mikserFirmaId != null && String(x.mikserFirmaId) === String(INOSEL_ID);
        const isOurPompa = x.pompaFirmaId != null && String(x.pompaFirmaId) === String(INOSEL_ID);
        if (isOurMixer || isOurPompa) return;
        const t = asText(x.tesis);
        if (!t) return;
        const v = pickMiktar(x);
        digerByTesis.set(t, (digerByTesis.get(t) || 0) + v);
      });
      */

        const loderByTesis = new Map();
      zoneSnap.docs.forEach((d) => {
        const x = d.data() || {};
        const deletedBy = x.deleted === true;
        if (deletedBy) return;
        const t = asText(x.tesis);
        if (!t) return;
        const v = safeNum(x.satisM3);
        loderByTesis.set(t, (loderByTesis.get(t) || 0) + v);
      });

      const tesisSet = new Set([
        ...Array.from(pompaByTesis.keys()),
        ...Array.from(mikserByTesis.keys()),
        ...Array.from(loderByTesis.keys()),
      ]);

      const rows = Array.from(tesisSet)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b, "tr"))
        .map((tesis) => {
          const pompalar = pompaByTesis.get(tesis) || 0;
          const mikserler = mikserByTesis.get(tesis) || 0;
          const loderler = loderByTesis.get(tesis) || 0;
          return { tesis, pompalar, mikserler, loderler };
        });

      const totals = rows.reduce(
        (acc, r) => {
          acc.pompalar += r.pompalar || 0;
          acc.mikserler += r.mikserler || 0;
          acc.loderler += r.loderler || 0;
          return acc;
        },
        { pompalar: 0, mikserler: 0, loderler: 0 }
      );

      setOzetRows(rows);
      setOzetTotals(totals);
    } catch (e) {
      setOzetRows([]);
      setOzetTotals({ pompalar: 0, mikserler: 0, loderler: 0 });
    }
  };

  /* -------- FETCH -------- */

  const fetchRows = async () => {
    setLoading(true);

    // --- PLATES (TESİS) BİLGİLERİNİ ÇEK ---
    try {
      const qPlates = query(collection(db, "plates"), where("active", "==", true));
      const snapPlates = await getDocs(qPlates);
      const pMap = {};
      snapPlates.docs.forEach(d => {
        const data = d.data();
        // Plakayı temizleyip (06ABC123 gibi) tesis adıyla eşleştiriyoruz
        const p = normPlate(data.plate);
        if (p) pMap[p] = data.tesis;
      });
      setPlateMap(pMap); // plateMap state'ini doldurur
    } catch (err) {
      console.error("Plates çekilemedi:", err);
    }

    // --- TESİS BİLGİLERİNİ ÇEKME BAŞLANGIÇ ---
    const qPlates = query(collection(db, "plates"), where("active", "==", true));
    const snapPlates = await getDocs(qPlates);
    const pMap = {};
    snapPlates.docs.forEach(d => {
      const data = d.data();
      pMap[normPlate(data.plate)] = data.tesis;
    }); 
    setPlateMap(pMap);
    // --- TESİS BİLGİLERİNİ ÇEKME BİTİŞ ---

    // --- ZONE VE POMPA VERİLERİNİ ÇEKME BAŞLANGIÇ ---
// --- ZONE VE POMPA VERİLERİNİ ÇEKME GÜNCELLENDİ ---
    const dateStr = filterDate;
    const qZone = query(collection(db, "hazirbeton_zone_uretim"), where("tarihStr", "==", dateStr));
    const snapZone = await getDocs(qZone);
    
    let zM_count = 0, zM_m3 = 0, zP_count = 0, zP_m3 = 0;

    snapZone.docs.forEach(d => {
      const data = d.data();
      // Silinmiş kayıtları hedeften çıkarıyoruz
      if (data.deleted === true) return; 

      if (String(data.mikserFirmaId) === INOSEL_ID) {
        zM_count++;
        zM_m3 += Number(data.satisM3 || 0);
      }
      if (data.pompaPlaka && data.pompaPlaka.trim() !== "") {
        zP_count++;
        zP_m3 += Number(data.satisM3 || 0);
      }
    });
    setZoneStats({ mikserCount: zM_count, mikserM3: zM_m3, pompaCount: zP_count, pompaM3: zP_m3 });

    // Pompaları çekerken de silinmemiş ve onaylanmış olanları alalım
    const qPompa = query(collection(db, "hazirbeton_pompa_hakedis"), where("tarihStr", "==", dateStr));
    const snapPompa = await getDocs(qPompa);
    setPompaRows(snapPompa.docs.map(d => d.data()).filter(x => x.deleted !== true));
    // --- ZONE VE POMPA VERİLERİNİ ÇEKME BİTİŞ ---

    try {
      const snap = await getDocs(
        query(
          collection(db, "hazirbeton_mikser_sevkiyat"),
          where("deleted", "==", false),
          where("tarihStr", "==", filterDate)
        )
      );

      const list = snap.docs
        .map((d) => {
          const r = d.data();
          if (r.mikserFirmaId !== INOSEL_ID) return null;

          const plaka = asText(r.mikserPlaka).replace(/\s+/g, "").toUpperCase();

          return {
            _id: d.id,
            tamamlandi: !!r.tamamlandi,
            tarih: r.tarihStr || filterDate,
            tesis: r.tesis || "",
            irsaliyeNo: r.irsaliyeNo || "",
            musteriAdi: r.musteriAdi || "",
            santiyeAdi: r.santiyeAdi || "",
            mikserPlaka: formatPlate(r.mikserPlaka || ""),
            // Satış: m3 (number) alanını çek; fallback m
            m3: r.m3 ?? r.m ?? "",
            aciklama: r.aciklama || "",
            soforAdi: r.soforAdi || "",
            cikisKm: r.cikisKm ?? "",
            donusKm: r.donusKm ?? "",
            cikisSaat: r.cikisSaat || "",
            donusSaat: r.donusSaat || "",
            votoKm: r.votoKm ?? 0,
            lock: r.lock || {},
            siraNo: r.siraNo || "", // BURAYI EKLEDİK
          };
        })
.filter(Boolean);
list.sort((a, b) => {
        const aSira = String(a.siraNo || "").trim();
        const bSira = String(b.siraNo || "").trim();

        // 1. siraNo'su dolu olanları en başa (tepeye) al
        if (aSira !== "" && bSira === "") return -1;
        if (aSira === "" && bSira !== "") return 1;

        // 2. Her ikisinde de siraNo varsa, alfabetik/sayısal diz
        if (aSira !== "" && bSira !== "") {
          return aSira.localeCompare(bSira, "tr-TR", { numeric: true });
        }

        // 3. siraNo yoksa; Tesis ve İrsaliye'ye göre diz
        const tesisComp = String(a.tesis).localeCompare(String(b.tesis), "tr-TR");
        if (tesisComp !== 0) return tesisComp;

        const irsA = parseInt(String(a.irsaliyeNo || "").replace(/\D/g, "")) || 0;
        const irsB = parseInt(String(b.irsaliyeNo || "").replace(/\D/g, "")) || 0;
        return irsA - irsB;
      });

// --- SIRALAMA: sıraNo varsa öncelik sıraNo, yoksa eski (TESİS + İRSALİYE) ---
const sortByTesisIrs = (a, b) => {
  const tA = String(a.tesis || "").toUpperCase();
  const tB = String(b.tesis || "").toUpperCase();
  if (tA < tB) return -1;
  if (tA > tB) return 1;

  const nA = Number((a.irsaliyeNo || "").replace(/\D/g, "")) || 0;
  const nB = Number((b.irsaliyeNo || "").replace(/\D/g, "")) || 0;
  return nA - nB;
};

const asTextLocal = (v) => (v == null ? "" : String(v));
const hasAnySiraNo = list.some((x) => asTextLocal(x.siraNo).trim() !== "");
const hasAllSiraNo = list.length > 0 && list.every((x) => asTextLocal(x.siraNo).trim() !== "");

if (!hasAnySiraNo) {
  list.sort(sortByTesisIrs);
} else if (hasAllSiraNo) {
  list.sort((a, b) => asTextLocal(a.siraNo).localeCompare(asTextLocal(b.siraNo), "tr-TR"));
} else {
  list.sort((a, b) => {
    const aHas = asTextLocal(a.siraNo).trim() !== "";
    const bHas = asTextLocal(b.siraNo).trim() !== "";
    if (aHas && bHas) return asTextLocal(a.siraNo).localeCompare(asTextLocal(b.siraNo), "tr-TR");
    if (aHas && !bHas) return -1;
    if (!aHas && bHas) return 1;
    return sortByTesisIrs(a, b);
  });
}

// --- ÖNCEKİ GÜN KM (plaka bazlı): seçilen tarihten önceki EN SON tarihin, EN SON sıraNo'lu satırının Dönüş KM'si ---
try {
  const pad2 = (n) => String(n).padStart(2, "0");
  const addDaysLocal = (ymd, delta) => {
    const d = new Date(ymd + "T00:00:00");
    d.setDate(d.getDate() + delta);
    const y = d.getFullYear();
    const m = pad2(d.getMonth() + 1);
    const da = pad2(d.getDate());
    return `${y}-${m}-${da}`;
  };

  const histStart = addDaysLocal(filterDate, -120); // 120 gün geri tarama
  const histSnap = await getDocs(
    query(
      collection(db, "hazirbeton_mikser_sevkiyat"),
      where("deleted", "==", false),
      where("tarihStr", ">=", histStart),
      where("tarihStr", "<", filterDate),
      orderBy("tarihStr", "desc"),
      limit(5000)
    )
  );

  const siraIdxFrom = (s) => {
    const m = asTextLocal(s).match(/_(\d+)$/);
    return m ? Number(m[1]) : -1;
  };

  const hist = histSnap.docs
    .map((d) => ({ _docId: d.id, ...(d.data() || {}) }))
    .filter((r) => r && r.deleted !== true && r.mikserFirmaId === INOSEL_ID);

  // plaka -> { lastDate, bestIdx, bestDonusKm }
  const bestByPlate = new Map();

  hist.forEach((r) => {
    const p = normPlate(r.mikserPlaka);
    if (!p) return;
    const date = asTextLocal(r.tarihStr);
    if (!date) return;

    const cur = bestByPlate.get(p);

    const idx = siraIdxFrom(r.siraNo);
    const dk = safeNum(r.donusKm);

    // önce en yeni tarih, sonra en büyük sıraNo index, sonra en büyük donusKm
    if (!cur) {
      bestByPlate.set(p, { lastDate: date, bestIdx: idx, bestDonusKm: dk });
      return;
    }

    if (date > cur.lastDate) {
      bestByPlate.set(p, { lastDate: date, bestIdx: idx, bestDonusKm: dk });
      return;
    }

    if (date === cur.lastDate) {
      if (idx > cur.bestIdx) {
        bestByPlate.set(p, { lastDate: date, bestIdx: idx, bestDonusKm: dk });
        return;
      }
      if (idx === cur.bestIdx) {
        const curDk = cur.bestDonusKm;
        if (dk != null && (curDk == null || dk > curDk)) {
          bestByPlate.set(p, { lastDate: date, bestIdx: idx, bestDonusKm: dk });
        }
      }
    }
  });

  const mapObj = {};
  bestByPlate.forEach((v, p) => {
    if (v && v.bestDonusKm != null) {
      mapObj[p] = { forDate: filterDate, lastDate: v.lastDate, minCikisKm: v.bestDonusKm };
    }
  });

  setPrevDayKmByPlate(mapObj);

  // --- OTOMATİK: her plakanın ilk satırına (tablo sıralamasındaki ilk satır) minKM'yi Çıkış KM olarak bas ---
  const firstIdxByPlate = new Map();
  list.forEach((row, i) => {
    const p = normPlate(row.mikserPlaka);
    if (!p) return;
    if (!firstIdxByPlate.has(p)) firstIdxByPlate.set(p, i);
  });

  firstIdxByPlate.forEach((i, p) => {
    const minInfo = mapObj[p];
    const minKm = minInfo ? safeNum(minInfo.minCikisKm) : null;
    if (minKm == null) return;

    const row = list[i];
    const curCikis = safeNum(row.cikisKm);

    // sadece boşsa ya da küçükse otomatik düzelt (kullanıcı daha büyük yazarsa dokunma)
    if (curCikis == null || curCikis < minKm) {
      list[i] = { ...row, cikisKm: minKm };
    }
  });
} catch (e) {
  // history fetch fail -> sadece mevcut günü göster, prevDayKm boş kalsın
  setPrevDayKmByPlate({});
}

setRows(list);

      // Sağ üst özet tablo (grid ile aynı anda güncellensin)
      await buildOzetFromData(list, filterDate);

    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (Object.keys(plateToTesis).length) fetchRows();
    // eslint-disable-next-line
  }, [plateToTesis]);

  const spaceShortcutRef = useRef(false);

  useEffect(() => {
    const isTypingTarget = (target) => {
      const tag = String(target?.tagName || "").toLowerCase();
      const type = String(target?.type || "").toLowerCase();
      return (tag === "input" && type !== "date") || tag === "textarea" || tag === "select" || target?.isContentEditable;
    };
    const onKeyDown = (e) => {
      if (e.code === "Space" && !isTypingTarget(e.target)) {
        spaceShortcutRef.current = true;
        return;
      }
      if (e.key !== "Enter" || isTypingTarget(e.target)) return;
      e.preventDefault();
      if (spaceShortcutRef.current) handleSave();
      else fetchRows();
    };
    const onKeyUp = (e) => {
      if (e.code === "Space") spaceShortcutRef.current = false;
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
    // eslint-disable-next-line
  }, [loading, saving, filterDate, rows, filters]);

  /* -------- FILTER -------- */

const filteredRows = useMemo(() => {
    return rows
      .filter((r) =>
        Object.entries(filters).every(([key, values]) => {
          if (!values || values.length === 0) return true;
          return values.includes(String(r[key] ?? ""));
        })
      )
      // siraNo'ya göre küçükten büyüğe diziyoruz (KM zinciri için şart)
      .sort((a, b) => (a.siraNo || "").localeCompare(b.siraNo || ""));
  }, [rows, filters]);

const progress = useMemo(() => {
    // --- MİKSER HESABI ---
    const activeMikserler = rows.filter(r => !r.deleted);
    const completedMikserler = activeMikserler.filter(r => r.tamamlandi === true);

    // --- POMPA HESABI (GRUPLAYARAK 45'E DÜŞÜRME) ---
    const uniqueIrsaliyeler = {};
    pompaRows.forEach(r => {
      if (r.deleted) return;
      // İrsaliye No yoksa bile Plaka+Şantiye ile grupla ki 124'ten kurtulalım
      const irsKey = r.irsaliyeNo || `${normPlate(r.pompaPlaka)}_${asText(r.santiyeAdi)}`;
      
      if (!uniqueIrsaliyeler[irsKey]) {
        uniqueIrsaliyeler[irsKey] = { done: false };
      }
      if (r.tamamlandi === true) {
        uniqueIrsaliyeler[irsKey].done = true;
      }
    });

    const pompaValues = Object.values(uniqueIrsaliyeler);
    const totalP = pompaValues.length; // ARTIK 45 BURASI
    const doneP = pompaValues.filter(v => v.done).length; // ARTIK 23 BURASI

    return {
      // Panelde görünecek yazılar:
      mikserLabel: `${completedMikserler.length} / ${zoneStats.mikserCount}`,
      pompaLabel: `${doneP} / ${totalP}`, // <--- BURASI 124'Ü 45 YAPACAK
      
      // Çubukların doluluk oranları:
      mikserYuzde: zoneStats.mikserCount > 0 ? (completedMikserler.length / zoneStats.mikserCount) * 100 : 0,
      pompaYuzde: totalP > 0 ? (doneP / totalP) * 100 : 0,
    };
  }, [rows, pompaRows, zoneStats]);

  const toggleFilter = (key, value) => {
    setFilters((prev) => {
      const cur = prev[key] || [];
      return {
        ...prev,
        [key]: cur.includes(value)
          ? cur.filter((v) => v !== value)
          : [...cur, value],
      };
    });
  };

  const openFilter = (key, e) => {
    setOpenFilterKey(key);
    setFilterAnchor(e.currentTarget.getBoundingClientRect());
  };

  const filterValues = (key) => uniq(rows.map((r) => String(r[key] ?? "")));

  
  /* -------- KM VALIDATION (sıraNo + önceki gün min KM) -------- */

  const computeIssuesByOrder = (rowsToCheck) => {
    const reasonsById = new Map(); // id -> Set(reasons)

    const addReason = (id, reason) => {
      if (!reasonsById.has(id)) reasonsById.set(id, new Set());
      reasonsById.get(id).add(reason);
    };

    const getRowDate = (r) => asText(r.tarihStr || filterDate);

    const getSiraIdx = (r, fallback) => {
      const s = asText(r.siraNo);
      const m = s.match(/_(\d+)$/);
      if (m) return Number(m[1]);
      return fallback;
    };

    const grouped = {};
    rowsToCheck.forEach((r, i) => {
      const pNorm = normPlate(r.mikserPlaka);
      const key = `${asText(r.tesis)}__${pNorm}`;
      if (!pNorm) return;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push({ ...r, _orderIdx: i });
    });

    Object.values(grouped).forEach((list) => {
      const inOrder = [...list].sort((a, b) => {
        const da = getRowDate(a);
        const dbb = getRowDate(b);
        if (da !== dbb) return da.localeCompare(dbb); // tarih asc
        const sa = getSiraIdx(a, a._orderIdx + 1);
        const sb = getSiraIdx(b, b._orderIdx + 1);
        if (sa !== sb) return sa - sb;
        return (a._orderIdx ?? 0) - (b._orderIdx ?? 0);
      });

      for (let i = 0; i < inOrder.length; i++) {
        const cur = inOrder[i];
        const pNorm = normPlate(cur.mikserPlaka);
        const rowDate = getRowDate(cur);

        const cikis = safeNum(cur.cikisKm);
        const donus = safeNum(cur.donusKm);

        // (0) önceki gün minimum KM (sadece seçili gün için)
        const minInfo = prevDayKmByPlate?.[pNorm];
        const minKm =
          minInfo && asText(minInfo.forDate) === asText(rowDate)
            ? safeNum(minInfo.minCikisKm)
            : null;

        if (minKm != null) {
          if (cikis != null && cikis < minKm) {
            addReason(
              cur._id,
              `KM minimum: ${minKm} (önceki gün ${asText(minInfo.lastDate)} max Dönüş KM) - Çıkış KM daha küçük olamaz`
            );
          }
          if (donus != null && donus < minKm) {
            addReason(
              cur._id,
              `KM minimum: ${minKm} (önceki gün ${asText(minInfo.lastDate)} max Dönüş KM) - Dönüş KM daha küçük olamaz`
            );
          }
        }

        // (1) aynı satır: donus > cikis
        if (cikis != null && donus != null && donus < cikis) {
          addReason(cur._id, "Dönüş KM, Çıkış KM’den büyük olmalı");
        }

        // (2) ardışık: önceki donus <= sonraki cikis
        if (i > 0) {
          const prev = inOrder[i - 1];
          const prevDonus = safeNum(prev.donusKm);
          if (prevDonus != null && cikis != null && prevDonus > cikis) {
            addReason(
              cur._id,
              "KM zinciri: önceki satırın Dönüş KM’si bu satırın Çıkış KM’sinden büyük"
            );
          }
        }

        // (3) bu satırın KM'leri, bir sonraki satırın Çıkış KM'sini aşamaz (aynı gün/plaka akışı)
        if (i < inOrder.length - 1) {
          const next = inOrder[i + 1];
          const nextCikis = safeNum(next.cikisKm);

          if (donus != null && nextCikis != null && donus > nextCikis) {
            addReason(
              cur._id,
              "KM zinciri: bu satırın Dönüş KM’si bir sonraki satırın Çıkış KM’sinden büyük olamaz"
            );
          }
          if (cikis != null && nextCikis != null && cikis > nextCikis) {
            addReason(
              cur._id,
              "KM zinciri: bu satırın Çıkış KM’si bir sonraki satırın Çıkış KM’sinden büyük olamaz"
            );
          }
        }

        // Saat Kontrolü: Çıkış saati Dönüş saatinden büyükse hata ver
const sStart = parseHHMM(cur.cikisSaat);
const sEnd = parseHHMM(cur.donusSaat);
if (sStart !== null && sEnd !== null && sStart > sEnd) {
  addReason(cur._id, "Dönüş saati çıkıştan önce olamaz");
}
      }
    });

    const badIds = [...reasonsById.keys()];
    const reasonsObj = {};
    for (const [id, set] of reasonsById.entries()) {
      reasonsObj[id] = [...set];
    }
    return { badIds, reasonsObj };
  };


  useEffect(() => {
    setBadRowIds(computeIssuesByOrder(rows).badIds);
    // eslint-disable-next-line
  }, [rows]);

  /* -------- UPDATE CELL (ID bazlı + KM zinciri ekran sırasına göre) -------- */

  const updateCell = (rowId, key, val) => {
    setRows((prev) => {
      const idx = prev.findIndex((x) => x._id === rowId);
      if (idx < 0) return prev;
      if (prev[idx]?.tamamlandi === true) return prev;
const copy = [...prev];
      const r = { ...copy[idx] };

      // KM zinciri için: bu satırın önceki Dönüş KM (eski değer)
      const prevDonusKmNum = safeNum(r.donusKm);

      if (key === "cikisSaat" || key === "donusSaat") {
        r[key] = formatTimeSmart(val);
      } else if (key === "mikserPlaka") {
        r[key] = formatPlate(val);
      } else {
        r[key] = val;
      }

      copy[idx] = r;

      // Plaka seçildiğinde (veya değiştiğinde) seçili tarihten önceki SON günün max Dönüş KM'sini çekip,
      // eğer bu satır seçili tarihte aynı plakanın ilk satırıysa ve Çıkış KM boşsa otomatik bas.
      if (key === "mikserPlaka") {
        const pNormNow = normPlate(r.mikserPlaka);
        const dStr = asText(r.tarihStr || filterDate);
        if (pNormNow && dStr) {
          (async () => {
            const info = await fetchPrevDayMaxDonusKm(pNormNow, dStr);
            if (!info || info.minCikisKm == null) return;

            setRows((prev2) => {
              // aynı tarihte aynı plakanın ilk satırını bul (ekran sırası)
              let firstId = null;
              for (const rr of prev2) {
                if (asText(rr.tarihStr || filterDate) !== dStr) continue;
                if (normPlate(rr.mikserPlaka) === pNormNow) {
                  firstId = rr._id;
                  break;
                }
              }
              if (!firstId || firstId !== rowId) return prev2;

              const idx2 = prev2.findIndex((x) => x._id === rowId);
              if (idx2 < 0) return prev2;
              if (safeNum(prev2[idx2].cikisKm) != null) return prev2;

              const cp = [...prev2];
              cp[idx2] = { ...cp[idx2], cikisKm: String(info.minCikisKm) };
              return cp;
            });
          })();
        }
      }


      // KM zinciri: donusKm değişince -> bir sonraki satırın cikisKm'si boşsa veya önceki otomatik değerse güncelle
      if (key === "donusKm") {
        const donusKmNum = safeNum(val);
        if (donusKmNum != null) {
          const tesis = r.tesis;
          const plakaNorm = asText(r.mikserPlaka)
            .replace(/\s+/g, "")
            .toUpperCase();

          for (let j = idx + 1; j < copy.length; j++) {
            const pj = asText(copy[j].mikserPlaka)
              .replace(/\s+/g, "")
              .toUpperCase();
            if (copy[j].tesis === tesis && pj === plakaNorm) {
              const curNextCikisNum = safeNum(copy[j].cikisKm);
              if (
                copy[j].cikisKm === "" ||
                copy[j].cikisKm == null ||
                (prevDonusKmNum != null && curNextCikisNum === prevDonusKmNum)
              ) {
                // raw string yaz (120 yazarken 1'de kalmasın)
                copy[j] = { ...copy[j], cikisKm: val };
              }
              break;
            }
          }
        }
      }

      return copy;
    });
  };

  // Şoför kopyalama: sadece kullanıcı girişini tamamlayınca (blur) alt satıra kopyala (boşsa)
  const propagateSoforToNext = (rowId, val) => {
    const v = asText(val);
    if (!v) return;

    setRows((prev) => {
      const idx = prev.findIndex((x) => x._id === rowId);
      if (idx < 0) return prev;

      const copy = [...prev];
      const r = copy[idx];

      const tesis = r.tesis;
      const plakaNorm = asText(r.mikserPlaka).replace(/\s+/g, "").toUpperCase();

      for (let j = idx + 1; j < copy.length; j++) {
        const pj = asText(copy[j].mikserPlaka).replace(/\s+/g, "").toUpperCase();
        if (copy[j].tesis === tesis && pj === plakaNorm) {
          if (copy[j].tamamlandi !== true && !asText(copy[j].soforAdi)) {
            copy[j] = { ...copy[j], soforAdi: v };
          }
          break;
        }
      }
      return copy;
    });
  };

  /* -------- DRAG & DROP (sadece görünen satırlar içinde yeniden sıralar) -------- */

const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const visibleIds = filteredRows.map((r) => r._id);
    if (!visibleIds.includes(active.id) || !visibleIds.includes(over.id)) return;

    const oldIndex = visibleIds.indexOf(active.id);
    const newIndex = visibleIds.indexOf(over.id);
    const newVisibleIds = arrayMove(visibleIds, oldIndex, newIndex);

    setRows((prev) => {
      const pos = [];
      const idToRow = new Map(prev.map((r) => [r._id, r]));
      prev.forEach((r, i) => {
        if (visibleIds.includes(r._id)) pos.push(i);
      });

      const next = [...prev];
      for (let k = 0; k < pos.length; k++) {
        const currentRow = idToRow.get(newVisibleIds[k]);
        
        // --- Otomatik SiraNo Atama ---
        const yeniSira = k + 1;
        const padSira = String(yeniSira).padStart(2, "0");
        const cleanTarih = currentRow.tarihStr ? currentRow.tarihStr.split("-").reverse().join("") : "";
        const cleanPlaka = (currentRow.mikserPlaka || "").replace(/\s+/g, "").toUpperCase();
        
        next[pos[k]] = {
          ...currentRow,
          siraNo: `${cleanTarih}_${cleanPlaka}_${padSira}`
        };
      }
      return next;
    });
  };

  /* -------- SAVE -------- */

  // GLOBAL IRSALIYE NO KONTROLÜ (tarihten bağımsız)
  // - Firestore'da (deleted:false) aynı irsaliyeNo varsa ve doc id farklıysa: duplicate
  // - Aynı ekranda birden fazla aynı irsaliyeNo varsa: duplicate
  const computeIrsaliyeDuplicateReasons = async (rowsToCheck) => {
    const reasonsById = new Map(); // rowId -> reason string

    const norm = (v) => asText(v).replace(/\s+/g, "");

    // (A) ekran içi tekrar kontrolü
    const seen = new Map(); // irs -> firstRowId
    rowsToCheck.forEach((r) => {
      const irs = norm(r.irsaliyeNo);
      if (!irs) return;
      if (seen.has(irs)) {
        reasonsById.set(r._id, "İrsaliye No ekranda tekrarlı");
        // ilk kaydı da işaretle
        const firstId = seen.get(irs);
        if (firstId) reasonsById.set(firstId, "İrsaliye No ekranda tekrarlı");
      } else {
        seen.set(irs, r._id);
      }
    });

    // (B) veritabanı tekrar kontrolü (tarihten bağımsız)
    const uniqIrsList = uniq(rowsToCheck.map((r) => norm(r.irsaliyeNo))).filter(Boolean);

    const chunks = [];
    for (let i = 0; i < uniqIrsList.length; i += 10) chunks.push(uniqIrsList.slice(i, i + 10));

    const irsToDocIds = new Map(); // irs -> Set(docId)
    for (const chunk of chunks) {
      const snap = await getDocs(
        query(
          collection(db, "hazirbeton_mikser_sevkiyat"),
          where("deleted", "==", false),
          where("irsaliyeNo", "in", chunk)
        )
      );
      snap.docs.forEach((d) => {
        const data = d.data() || {};
        const irs = norm(data.irsaliyeNo);
        if (!irs) return;
        if (!irsToDocIds.has(irs)) irsToDocIds.set(irs, new Set());
        irsToDocIds.get(irs).add(d.id);
      });
    }

    rowsToCheck.forEach((r) => {
      const irs = norm(r.irsaliyeNo);
      if (!irs) return;
      const ids = irsToDocIds.get(irs);
      if (!ids) return;

      // aynı irsaliyeNo ile başka bir doc varsa (kendisi hariç)
      const hasOther = [...ids].some((id) => id !== r._id);
      if (hasOther) {
        reasonsById.set(r._id, "İrsaliye No sistemde zaten kayıtlı");
      }
    });

    return reasonsById;
  };

  const handleSave = async () => {
    if (saving) return;
    console.log("=== SAVE BAŞLADI ===");
console.log("prevDayKmByPlate:", prevDayKmByPlate);

    const { reasonsObj } = computeIssuesByOrder(rows);
// 🔒 GARANTİ KM MİNİMUM KONTROLÜ
for (const row of rows) {
  const p = normPlate(row.mikserPlaka);
  console.log("Plate:", p);

  if (!p) continue;

 const minInfo =
  prevDayKmByPlate[p] ||
  prevDayKmByPlate[formatPlate(p)] ||
  prevDayKmByPlate[p.replace(/\s+/g, "")];

  console.log("MinInfo:", minInfo);

  if (!minInfo) continue;

  const minKm = safeNum(minInfo.minCikisKm);
  const cikisKm = safeNum(row.cikisKm);
  const donusKm = safeNum(row.donusKm);

  if (minKm != null) {
    if (cikisKm != null && cikisKm < minKm) {
      window.alert(
        `${row.mikserPlaka} plakası için Çıkış KM (${cikisKm}), 
        ${minInfo.lastDate} tarihindeki Dönüş KM (${minKm})'den küçük olamaz.`
      );
      return;
    }

    if (donusKm != null && donusKm < minKm) {
      window.alert(
        `${row.mikserPlaka} plakası için Dönüş KM (${donusKm}), 
        ${minInfo.lastDate} tarihindeki Dönüş KM (${minKm})'den küçük olamaz.`
      );
      return;
    }
  }
}

    // KM minimum kuralı (önceki gün max Dönüş KM) ihlali varsa kesinlikle kaydetme


    const editableRows = rows.filter((r) => r.tamamlandi !== true);

    const irsaliyeDupReasons = await computeIrsaliyeDuplicateReasons(editableRows);
// Zorunlu alan kontrolü + hata nedenleri
    const requiredMissingReasons = (r) => {
      const missing = [];
      if (!asText(r.soforAdi)) missing.push("Şoför");
      if (safeNum(r.cikisKm) == null) missing.push("Çıkış KM");
      if (safeNum(r.donusKm) == null) missing.push("Dönüş KM");
      if (!asText(r.cikisSaat)) missing.push("Çıkış Saat");
      if (!asText(r.donusSaat)) missing.push("Dönüş Saat");
      return missing.length ? `Zorunlu alanlar eksik: ${missing.join(", ")}` : "";
    };

    const goodRows = [];
    const badRows = [];

    for (const r of editableRows) {
      const missingReason = requiredMissingReasons(r);
      const kmReasons = reasonsObj[r._id] || [];
      const irsDup = irsaliyeDupReasons.get(r._id) || "";
      const allReasons = [];
      if (missingReason) allReasons.push(missingReason);
      if (irsDup) allReasons.push(irsDup);
      allReasons.push(...kmReasons);

      if (allReasons.length === 0) {
        goodRows.push(r);
      } else {
        badRows.push({ ...r, _errorReason: allReasons.join(" | ") });
      }
    }

    if (badRows.length > 0) {
      const ok = window.confirm(
        "Hatalı veya eksik satırlar var. Yine de kaydetmek istiyor musunuz?"
      );
      if (!ok) return;
    }

    setLoading(true);
    setSaving(true);
    const email = auth.currentUser?.email || "";


// sıraNo: ekrandaki mevcut sıralamaya göre (gri + beyaz tüm satırlar)
const ddmmyyyy = String(filterDate || "").split("-").reverse().join("");


const counters = new Map(); // plaka -> sayaç
const siraNoById = new Map(); // rowId -> sıraNo

rows.forEach((rr) => {
  const p = normPlate(rr.mikserPlaka);
  const cur = (counters.get(p) || 0) + 1;
  counters.set(p, cur);

  const sira = String(cur).padStart(2, "0");
  siraNoById.set(rr._id, `${ddmmyyyy}_${p}_${sira}`);
});

for (const r of goodRows) {
      await updateDoc(doc(db, "hazirbeton_mikser_sevkiyat", r._id), {
        // Kullanıcının değiştirebildiği tüm alanlar (Fark & Süre hariç)
        tesis: r.tesis || "",
        tarihStr: r.tarih || "",
        siraNo: siraNoById.get(r._id) || "",
        irsaliyeNo: r.irsaliyeNo || "",
        musteriAdi: r.musteriAdi || "",
        santiyeAdi: r.santiyeAdi || "",
        mikserPlaka: formatPlate(r.mikserPlaka || ""),

        soforAdi: r.soforAdi,
        cikisKm: safeNum(r.cikisKm),
        donusKm: safeNum(r.donusKm),
        cikisSaat: r.cikisSaat,
        donusSaat: r.donusSaat,

        m3: safeNum(r.m3),
        aciklama: r.aciklama || "",

        siraNo: siraNoById.get(r._id) || "",
        tamamlandi: true,
        updatedAt: serverTimestamp(),
        updatedBy: email,
      });
    }


// ✅ Gri (tamamlandi:true) satırlarda da sıraNo güncellensin
// (Diğer alanlara dokunmadan)
for (const rr of rows) {
  if (rr.tamamlandi === true) {
    await updateDoc(doc(db, "hazirbeton_mikser_sevkiyat", rr._id), {
      siraNo: siraNoById.get(rr._id) || "",
      updatedAt: serverTimestamp(),
      updatedBy: email,
    });
  }
}


    // ✅ Hatalı satırlar Excel olarak ver
    if (badRows.length > 0) {
      downloadExcel(badRows);
    }

    await releaseRowLocks(Array.from(lockOwnedRef.current));

    alert("Kaydedildi");
    fetchRows();
    setLoading(false);
    setSaving(false);
  };

  /* ================= UI ================= */

const headers = [
    { key: "tesis", label: "Tesis", width: 80 },
    { key: "tarih", label: "Tarih", width: 90 },
    { key: "irsaliyeNo", label: "İrsaliye No", width: 160 }, // Etiketi kısalttık
    { key: "musteriAdi", label: "Müşteri", width: 120 },
    { key: "santiyeAdi", label: "Şantiye", width: 300 },
    { key: "mikserPlaka", label: "Plaka", width: 105 },
    { key: "m3", label: "m3", width: 60 },           // Etiketi kısalttık
    { key: "cikisSaat", label: "Çıkış Saat", width: 90 }, // Etiketi kısalttık
    { key: "cikisKm", label: "Çıkış KM", width: 110 },     // Etiketi kısalttık
    { key: "donusSaat", label: "Dönüş Saat", width: 90 },
    { key: "donusKm", label: "Dönüş KM", width: 110 },
    { key: "fark", label: "Fark", width: 80 },
    { key: "sure", label: "Süre", width: 70 },
    { key: "soforAdi", label: "Şoför", width: 150 },
    { key: "aciklama", label: "Açıklama", width: 200 },
  ];

return (
    <div style={{ padding: 16, position: "relative" }}>
      
      {/* Üst Başlık ve İlerleme Paneli Yan Yana */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: 20,
        flexWrap: 'wrap',
        gap: '10px'
      }}>
        <h2 style={{ margin: 0 }}>Çarşaf Sevkiyat</h2>

        {/* İLERLEME TAKİP PANELİ */}
     {/*   <div style={{ 
          display: 'flex', 
          gap: '15px', 
          padding: '10px 15px', 
          background: '#f8fafc', 
          borderRadius: '8px', 
          border: '1px solid #e2e8f0',
          minWidth: '400px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          position: 'absolute',
          top: 12,
          right: 1050, // Özet tablonun genişliği yaklaşık 450px olduğu için onu 480 yaptık
          zIndex: 100
        }}> */}
        <div style={{ 
  display: 'flex', 
  gap: '15px', 
  padding: '10px 15px', 
  background: '#f8fafc', 
  borderRadius: '8px', 
  border: '1px solid #e2e8f0',
  minWidth: '400px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  // DEĞİŞEN KISIMLAR:
  position: 'relative', // absolute yerine relative yaptık
  top: 0,               // top değerini sıfırladık veya ihtiyaca göre padding verdik
  //margin: '10px 0',     // Etrafına boşluk ekledik
  margin: '10px 700px 10px 0',
  zIndex: 100
}}>
          {/* Mikser Bölümü */}
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
              <span style={{ fontWeight: '600' }}>🚚 Mikser: {rows.filter(r => r.tamamlandi && !r.deleted).length}/{zoneStats.mikserCount}</span>
              <span style={{ color: '#2563eb', fontWeight: 'bold' }}>%{progress.mikserYuzde.toFixed(0)}</span>
            </div>
            <div style={{ width: '100%', background: '#e2e8f0', height: '6px', borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{ width: `${Math.min(progress.mikserYuzde, 100)}%`, background: '#3b82f6', height: '100%', transition: 'width 0.5s' }} />
            </div>
          </div>

          <div style={{ width: '1px', background: '#e2e8f0', alignSelf: 'stretch' }}></div>

          {/* Pompa Bölümü */}
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
              <span style={{ fontWeight: '600' }}>⛽ Pompa: {progress.pompaLabel}</span>
              <span style={{ color: '#ea580c', fontWeight: 'bold' }}>%{progress.pompaYuzde.toFixed(0)}</span>
            </div>
            <div style={{ width: '100%', background: '#e2e8f0', height: '6px', borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{ width: `${Math.min(progress.pompaYuzde, 100)}%`, background: '#f97316', height: '100%', transition: 'width 0.5s' }} />
            </div>
          </div>
        </div>
      </div>

      {/* Filtre ve Butonlar Bölümü */}
      <div style={{ marginBottom: 10, display: 'flex', gap: '10px', alignItems: 'center' }}>
        <input
          type="date"
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
          style={{ padding: '4px 8px' }}
        />
        <button onClick={fetchRows} disabled={loading} style={hbButtonStyle("secondary", loading)} title="Enter">
          {loading ? "Yükleniyor..." : "Getir"}
        </button>
      </div>

      {/* Sağ üst özet tablo ve geri kalan içerik buraya gelecek... */}
{/* Sağ üst özet tablo */}
<div
  style={{
    position: "absolute",
    top: 12,
    right: 12,
    width: "fit-content",
    background: "white",
    border: "1px solid #ddd",
    borderRadius: 8,
    padding: 10,
    boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
    fontSize: 12,
  }}
>
  <div style={{ fontWeight: 700, marginBottom: 6 }}>Tesis Bazlı Özet</div>

  <table style={{ width: "100%", borderCollapse: "collapse" }}>
    <thead>
      <tr>
        <th style={ozetTh}></th>
        {ozetRows.map((r) => (
          <th key={r.tesis} style={ozetTh}>{r.tesis}</th>
        ))}
        <th style={ozetTh}>TOPLAM</th>
      </tr>
    </thead>

    <tbody>
      {/* 1. Satır – Mikserler */}
            <tr>
        <td style={{ ...ozetTd, fontWeight: 700, textAlign: "left" }}>Mikserlerimiz</td>
        {ozetRows.map((r) => (
          <td key={r.tesis} style={ozetTd}>{fmtTr(r.mikserler)}</td>
        ))}
        <td style={{ ...ozetTd, fontWeight: 700 }}>{fmtTr(ozetTotals.mikserler)}</td>
      </tr>


      {/* 2. Satır – Pompalar */}
      <tr>
        <td style={{ ...ozetTd, fontWeight: 700, textAlign: "left" }}>Pompalarımız</td>
        {ozetRows.map((r) => (
          <td key={r.tesis} style={ozetTd}>{fmtTr(r.pompalar)}</td>
        ))}
        <td style={{ ...ozetTd, fontWeight: 700 }}>{fmtTr(ozetTotals.pompalar)}</td>
      </tr>

      {/* 3. Satır – Diğer */}
      <tr>
        <td style={{ ...ozetTd, fontWeight: 700, textAlign: "left" }}>Loderler</td>
        {ozetRows.map((r) => (
          <td key={r.tesis} style={ozetTd}>{fmtTr(r.loderler)}</td>
        ))}
        <td style={{ ...ozetTd, fontWeight: 700 }}>{fmtTr(ozetTotals.loderler)}</td>
      </tr>

      {/* 4. Satır – GENEL TOPLAM */}

    </tbody>
  </table>
</div>

<div style={{ paddingTop: "100px" }}>
  <table style={{ width: "max-content", fontSize: 11, tableLayout: "fixed" }}>
<thead>
      <tr>


        {/* Mevcut headers.map döngüsü */}
        {headers.map((h) => {
          const isFiltered = filters[h.key] && filters[h.key].length > 0;
          return (
            <th
              key={h.key}
              style={{
                ...thStyle,
                width: h.width,
                maxWidth: h.width,
                backgroundColor: "#f8fafc",
                color: "#475569",
                fontWeight: "600",
                border: "1px solid #e2e8f0",
                position: "sticky",
                top: 0,
                zIndex: 10
              }}
            >
              <div style={{ 
                display: "flex", 
                justifyContent: "space-between", 
                alignItems: "center",
                padding: "0 2px" 
              }}>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {h.label}
                </span>
                <span
                  style={{
                    cursor: "pointer",
                    marginLeft: "4px",
                    fontSize: "10px",
                    color: isFiltered ? "#3b82f6" : "#cbd5e1", 
                    backgroundColor: isFiltered ? "#eff6ff" : "transparent",
                    padding: "2px 4px",
                    borderRadius: "4px"
                  }}
                  onClick={(e) => openFilter(h.key, e)}
                >
                  ▼
                </span>
              </div>
            </th>
          );
        })}
      </tr>
    </thead>
        <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext
            items={filteredRows.map((r) => r._id)}
            strategy={verticalListSortingStrategy}
          >
            <tbody>
              {filteredRows.map((r, rowIdx) => (
                <SortableRow
                  key={r._id}
                  row={{...r, _lockByOther: isLockByOther(r), _lockLabel: lockLabel(r), _lockTitle: lockTitle(r)}}
                  isBad={badRowIds.includes(r._id)}
                >

                  {/* TESİS BİLGİSİ (EL YAZISIYLA EKLEDİK) */}


  {/* Buradan sonra senin mevcut headers.map döngün devam edecek... */}

                  {headers.map((h) => {
                    if (h.key === "fark") {
                      return (
                        <div key={h.key} style={{ fontWeight: 700 }}>
                          {calcFark(r)}
                        </div>
                      );
                    }

                    if (h.key === "sure") {
                      return (
                        <div key={h.key} style={{ fontWeight: 700 }}>
                          {calcSure(r)}
                        </div>
                      );
                    }

                    return (
                      <input
                        key={h.key}
                        type={h.key === "tarih" ? "date" : "text"}
                        disabled={r.tamamlandi === true || isLockByOther(r)}
                        style={{
                          backgroundColor: isLockByOther(r)
                            ? "#dff7df"
                            : r.tamamlandi
                            ? "#f0f0f0"
                            : "white",
                          color: isLockByOther(r) ? "#1b5e20" : r.tamamlandi ? "#666" : "black",
                        }}
                        list={
                          h.key === "soforAdi"
                            ? "dl-soforler"
                            : h.key === "mikserPlaka"
                            ? "dl-plakalar"
                            : undefined
                        }
tabIndex={
  h.key === "cikisSaat"
    ? 1000 + rowIdx  // İlk önce Çıkış Saatleri (Yukarıdan aşağı 1000, 1001, 1002...)
    : h.key === "donusSaat"
    ? 2000 + rowIdx  // Sonra Çıkış KM (2000, 2001...)
    : h.key === "cikisKm"
    ? 4000 + rowIdx  // Sonra Dönüş KM
    : h.key === "donusKm"
    ? 5000 + rowIdx  // Sonra Fark
    : h.key === "soforAdi"
    ? 6000 + rowIdx
    : h.key === "fark"
    ? 7000 + rowIdx
    : -1
}
disabled={r.tamamlandi === true || isLockByOther(r)}
style={{
  backgroundColor: isLockByOther(r) ? "#dff7df" : r.tamamlandi ? "#f0f0f0" : "white",
  color: isLockByOther(r) ? "#1b5e20" : r.tamamlandi ? "#666" : "black",
  width: "100%",           // Kutuyu sütuna sığdırır
  boxSizing: "border-box", // Dışarı taşmasını engeller
  border: "none",          // Çizgileri kaldırıp yer açar
  padding: "2px 4px",      // İç boşluğu daraltır
  fontSize: "12px"         // Yazıyı küçültüp yer kazandırır
}}
                        onFocus={() => {
                          if (r.tamamlandi !== true && !isLockByOther(r) && !isLockByMe(r)) {
                            acquireRowLock(r._id);
                          }

                          // Şoför zincirini TAB ile çalıştırmak için:
                          // Eğer bu satırda şoför boşsa ve aynı tesis+plaka için bir önceki satırda şoför varsa,
                          // bu satıra kopyala.
                          if (
                            (h.key === "cikisKm" ||
                              h.key === "donusKm" ||
                              h.key === "cikisSaat" ||
                              h.key === "donusSaat") &&
                            !asText(r.soforAdi)
                          ) {
                            const plakaNorm = asText(r.mikserPlaka)
                              .replace(/\s+/g, "")
                              .toUpperCase();
                            const idx0 = rows.findIndex((x) => x._id === r._id);
                            if (idx0 > 0) {
                              for (let k = idx0 - 1; k >= 0; k--) {
                                const prev = rows[k];
                                const prevPlaka = asText(prev.mikserPlaka)
                                  .replace(/\s+/g, "")
                                  .toUpperCase();
                                if (
                                  prev.tesis === r.tesis &&
                                  prevPlaka === plakaNorm
                                ) {
                                  if (asText(prev.soforAdi)) {
                                    updateCell(r._id, "soforAdi", prev.soforAdi);
                                  }
                                  break;
                                }
                              }
                            }
                          }
                        }}
                        onBlur={
                          h.key === "soforAdi"
                            ? (e) => propagateSoforToNext(r._id, e.target.value)
                            : undefined
                        }
                        value={r[h.key] ?? ""}
                        onChange={(e) =>
                          updateCell(r._id, h.key, e.target.value)
                        }
                      />
                    );
                  })}
                </SortableRow>
              ))}
            </tbody>
          </SortableContext>
        </DndContext>
      </table>
</div>
      <datalist id="dl-soforler">
        {soforler.map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>

      <datalist id="dl-plakalar">
        {plakalar.map((p) => (
          <option key={p} value={p} />
        ))}
      </datalist>

      {openFilterKey && (
<FilterPopup
  anchor={filterAnchor}
  values={filterValues(openFilterKey)}
  selected={filters[openFilterKey] || []}
  onToggle={(v) => toggleFilter(openFilterKey, v)}
  onSelectAll={() =>
    setFilters((prev) => ({
      ...prev,
      [openFilterKey]: filterValues(openFilterKey),
    }))
  }
  onClear={() =>
    setFilters((prev) => ({
      ...prev,
      [openFilterKey]: [],
    }))
  }
  onClose={() => setOpenFilterKey(null)}
/>
      )}

      <div style={{ marginTop: 12 }}>
        <button onClick={handleSave} disabled={loading || saving} style={hbButtonStyle("primary", loading || saving)} title="Space + Enter">
          {saving ? "Kaydediliyor..." : "Kaydet"}
        </button>
      </div>
    </div>
  );
}

/* ================= STYLES ================= */


const ozetTh = {
  border: "1px solid #e0e0e0",
  padding: "6px 6px",
  background: "#fafafa",
  textAlign: "right",
  whiteSpace: "nowrap",
};

const ozetTd = {
  border: "1px solid #e0e0e0",
  padding: "6px 6px",
  textAlign: "right",
  whiteSpace: "nowrap",
};

const hbButtonStyle = (variant = "secondary", disabled = false) => ({
  border: "1px solid " + (variant === "primary" ? "#1d4ed8" : "#cbd5e1"),
  borderRadius: 7,
  padding: "7px 14px",
  fontWeight: 700,
  color: disabled ? "#94a3b8" : variant === "primary" ? "#fff" : "#0f172a",
  background: disabled
    ? "#f1f5f9"
    : variant === "primary"
      ? "linear-gradient(180deg, #3b82f6, #2563eb)"
      : "linear-gradient(180deg, #fff, #f8fafc)",
  boxShadow: disabled ? "none" : "0 1px 2px rgba(15,23,42,0.12)",
  cursor: disabled ? "not-allowed" : "pointer",
});

const thStyle = {
  border: "1px solid #ccc",
  background: "#f2f2f2",
  padding: "4px",
  fontSize: "12px",
  textAlign: "left",
  position: "sticky",
  top: 0,
  zIndex: 10,
  // BUNLARI EKLE:
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  boxSizing: "border-box"
};
const tdStyle = {
  border: "1px solid #e0e0e0",
  padding: 4,
};
